import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  CubeIcon,
  ShoppingBagIcon,
  ArrowPathIcon,
  BanknotesIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon,
  BuildingStorefrontIcon,
  CreditCardIcon,
  DocumentTextIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { labsAPI, productsAPI, ordersAPI, exchangeRequestsAPI, exchangeTransactionsAPI, getImageUrl } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const LabDashboard = () => {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [myProducts, setMyProducts] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  useEffect(() => {
    fetchDashboardData();
    fetchMyProducts();
    fetchMyOrders();
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await labsAPI.getMyDashboard();

      // البيانات في response.data.data بسبب هيكل API response
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProducts = async () => {
    try {
      const response = await productsAPI.getMyProducts({ limit: 6, sortBy: 'createdAt', sortOrder: 'desc' });
      setMyProducts(response.data.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchMyOrders = async () => {
    try {
      // استخدام النظام الجديد للتبادلات
      const [myOrders, asSellerOrders, sentExchangeRequests, receivedExchangeRequests, activeExchanges] = await Promise.all([
        ordersAPI.getMyOrders(),
        ordersAPI.getAsSeller(),
        exchangeRequestsAPI.getExchangeRequests({ type: 'sent' }),
        exchangeRequestsAPI.getExchangeRequests({ type: 'received' }),
        exchangeTransactionsAPI.getMyTransactions()
      ]);
      
      const buyerOrders = (myOrders.data?.data?.orders || myOrders.data?.orders || []).map(order => ({
        ...order,
        role: 'buyer',
        type: 'order',
        orderNumber: order.orderNumber
      }));
      
      const sellerOrders = (asSellerOrders.data?.data?.orders || asSellerOrders.data?.orders || []).map(order => ({
        ...order,
        role: 'seller',
        type: 'order',
        orderNumber: order.orderNumber
      }));

      // طلبات التبادل المرسلة (النظام الجديد)
      const sentRequestsRaw = sentExchangeRequests.data?.data || sentExchangeRequests.data || [];
      console.log('Sent exchange requests raw:', sentRequestsRaw);
      const sentRequests = sentRequestsRaw.map(request => ({
        ...request,
        role: 'requester',
        type: 'exchange_request',
        orderNumber: request.requestNumber,
        createdAt: request.createdAt
      }));
      
      // طلبات التبادل المستلمة (النظام الجديد)
      const receivedRequestsRaw = receivedExchangeRequests.data?.data || receivedExchangeRequests.data || [];
      console.log('Received exchange requests raw:', receivedRequestsRaw);
      const receivedRequests = receivedRequestsRaw.map(request => ({
        ...request,
        role: 'receiver',
        type: 'exchange_request',
        orderNumber: request.requestNumber,
        createdAt: request.createdAt
      }));

      // المعاملات النشطة (النظام الجديد) - تصفية الحالات النشطة فقط
      const allTransactions = activeExchanges.data?.data || activeExchanges.data || [];
      const activeTransactions = allTransactions
        .filter(transaction => 
          ['confirmed', 'shipping_a_to_b', 'shipping_b_to_a', 'both_shipping'].includes(transaction.status)
        )
        .map(transaction => ({
          ...transaction,
          role: 'participant',
          type: 'exchange_transaction',
          orderNumber: transaction.transactionNumber,
          createdAt: transaction.createdAt
        }));

      // دمج جميع الطلبات والترتيب حسب التاريخ
      const allOrders = [...buyerOrders, ...sellerOrders, ...sentRequests, ...receivedRequests, ...activeTransactions]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // تسجيل البيانات للتحقق
      console.log('Dashboard Data:', {
        sentRequests: sentRequests.length,
        receivedRequests: receivedRequests.length,
        activeTransactions: activeTransactions.length,
        totalExchangeData: sentRequests.length + receivedRequests.length + activeTransactions.length,
        allOrders: allOrders.length,
        exchangeOrders: allOrders.filter(order => order.type === 'exchange_request' || order.type === 'exchange_transaction').length
      });
      
      setMyOrders(allOrders);

      // حساب الإحصائيات الجديدة
      const totalExchangeRequests = sentRequests.length + receivedRequests.length;
      const activeExchangeTransactions = activeTransactions.length;
      const totalOrders = buyerOrders.length + sellerOrders.length;
      
      // تحديث بيانات الـ Dashboard لتشمل الإحصائيات الجديدة
      if (dashboardData) {
        setDashboardData(prev => ({
          ...prev,
          overview: {
            ...prev.overview,
            totalExchangeRequests: totalExchangeRequests,
            activeExchangeTransactions: activeExchangeTransactions,
            totalOrders: totalOrders
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      // في حالة فشل جلب البيانات، نعرض الطلبات العادية على الأقل
      try {
        const [myOrders, asSellerOrders] = await Promise.all([
          ordersAPI.getMyOrders(),
          ordersAPI.getAsSeller()
        ]);
        
        const buyerOrders = (myOrders.data?.data?.orders || myOrders.data?.orders || []).map(order => ({
          ...order,
          role: 'buyer',
          type: 'order',
          orderNumber: order.orderNumber
        }));
        
        const sellerOrders = (asSellerOrders.data?.data?.orders || asSellerOrders.data?.orders || []).map(order => ({
          ...order,
          role: 'seller',
          type: 'order',
          orderNumber: order.orderNumber
        }));

        const allOrders = [...buyerOrders, ...sellerOrders]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        setMyOrders(allOrders);
      } catch (fallbackError) {
        console.error('Error fetching fallback orders:', fallbackError);
        setMyOrders([]);
      }
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const { overview, periodStats, breakdown, recentActivities } = dashboardData || {};

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 300
      }
    }
  };

  // Stat cards data based on real data
  const statCards = [
    {
      id: 1,
      title: t('lab.totalRevenue'),
      value: `${(overview?.walletBalance || 0).toLocaleString()}.00`,
      icon: BanknotesIcon,
      color: 'blue',
      bgGradient: 'from-brand-blue to-brand-blueSecondary',
      bgIcon: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      id: 2,
      title: t('lab.pendingOrders'),
      value: overview?.totalOrders || 0,
      icon: ShoppingBagIcon,
      color: 'green',
      bgGradient: 'from-brand-green to-green-600',
      bgIcon: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      id: 3,
      title: t('labDashboard.exchangeRequestsCount'),
      value: overview?.totalExchangeRequests || 0,
      icon: ArrowPathIcon,
      color: 'amber',
      bgGradient: 'from-amber-500 to-amber-600',
      bgIcon: 'bg-amber-100',
      iconColor: 'text-amber-600'
    },
    {
      id: 4,
      title: t('labDashboard.activeExchanges'),
      value: overview?.activeExchangeTransactions || 0,
      icon: TruckIcon,
      color: 'indigo',
      bgGradient: 'from-indigo-500 to-indigo-600',
      bgIcon: 'bg-indigo-100',
      iconColor: 'text-indigo-600'
    },
    {
      id: 5,
      title: t('lab.availableProducts'),
      value: overview?.totalProducts || 0,
      icon: CubeIcon,
      color: 'purple',
      bgGradient: 'from-purple-500 to-purple-600',
      bgIcon: 'bg-purple-100',
      iconColor: 'text-purple-600'
    }
  ];

  // Chart data for orders status (real data)
  const ordersStatusData = {
    labels: [t('lab.orderStatus.pending'), t('lab.orderStatus.processing'), t('lab.orderStatus.delivered'), t('lab.orderStatus.cancelled')],
    datasets: [
      {
        data: [
          breakdown?.ordersByStatus?.pending || 0,
          breakdown?.ordersByStatus?.processing || 0,
          breakdown?.ordersByStatus?.completed || 0,
          breakdown?.ordersByStatus?.cancelled || 0
        ],
        backgroundColor: [
          'rgba(251, 191, 36, 0.8)', // Yellow for pending
          'rgba(59, 130, 246, 0.8)', // Blue for processing
          'rgba(34, 197, 94, 0.8)', // Green for completed
          'rgba(239, 68, 68, 0.8)'  // Red for cancelled
        ],
        borderColor: [
          'rgb(251, 191, 36)',
          'rgb(59, 130, 246)',
          'rgb(34, 197, 94)',
          'rgb(239, 68, 68)'
        ],
        borderWidth: 2
      }
    ]
  };

  // Chart data for products by type (real data)
  const productsTypeData = {
    labels: [t('products.forSale'), t('products.forExchange'), t('labDashboard.assets')],
    datasets: [
      {
        data: [
          breakdown?.productsByType?.sale || 0,
          breakdown?.productsByType?.exchange || 0,
          breakdown?.productsByType?.asset || 0
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)', // Green for sale
          'rgba(59, 130, 246, 0.8)', // Blue for exchange
          'rgba(168, 85, 247, 0.8)'  // Purple for assets
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(59, 130, 246)',
          'rgb(168, 85, 247)'
        ],
        borderWidth: 2
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: 'Cairo, sans-serif',
            size: 12
          },
          padding: 20
        }
      }
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div className="px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ChartBarIcon className="h-8 w-8 text-brand-blue" />
                {t('lab.dashboard')}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('lab.overview')}
              </p>
            </div>
            <div className="flex items-center space-x-4 space-x-reverse">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="week">{t('labDashboard.thisWeek')}</option>
                <option value="month">{t('labDashboard.thisMonth')}</option>
                <option value="quarter">{t('labDashboard.thisQuarter')}</option>
                <option value="year">{t('labDashboard.thisYear')}</option>
              </select>
              <button
                onClick={() => {
                  fetchDashboardData();
                  fetchMyProducts();
                  fetchMyOrders();
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <ArrowPathIcon className="h-4 w-4 icon-text" />
                {t('common.refresh')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Stat Cards */}
          <motion.div variants={itemVariants}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statCards.map((card) => (
                <motion.div
                  key={card.id}
                  whileHover={{ scale: 1.02, y: -5 }}
                  className="relative overflow-hidden bg-white rounded-2xl shadow-lg border border-gray-100"
                >
                  <div className="relative p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600 mb-1">{card.title}</p>
                        <p className="text-3xl font-bold text-gray-900 mb-2">{card.value}</p>
                      </div>
                      <div className={`p-3 rounded-xl ${card.bgIcon}`}>
                        <card.icon className={`h-6 w-6 ${card.iconColor}`} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Orders Status Chart */}
            <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('labDashboard.orderStates')}</h3>
              <div className="h-80">
                <Doughnut data={ordersStatusData} options={doughnutOptions} />
              </div>
            </motion.div>

            {/* Products Type Chart */}
            <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('labDashboard.productTypes')}</h3>
              <div className="h-80">
                <Doughnut data={productsTypeData} options={doughnutOptions} />
              </div>
            </motion.div>
          </div>

          {/* My Products Section */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <CubeIcon className="h-6 w-6 icon-text text-brand-blue" />
                {t('labDashboard.myProducts')}
              </h3>
              <a 
                href="/lab/products" 
                className="text-brand-blue hover:text-blue-700 text-sm font-medium flex items-center"
              >
                {t('common.viewAll')}
                <ArrowTrendingUpIcon className="h-4 w-4 icon-text" />
              </a>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myProducts.length === 0 ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <CubeIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">{t('labDashboard.noProducts')}</p>
                  <a 
                    href="/lab/products" 
                    className="mt-2 inline-block text-brand-blue hover:text-blue-700 text-sm font-medium"
                  >
                    {t('labDashboard.addNewProduct')}
                  </a>
                </div>
              ) : (
                myProducts.map((product) => (
                  <motion.div
                    key={product._id}
                    whileHover={{ scale: 1.02 }}
                    className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start space-x-3 space-x-reverse">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={getImageUrl(product.images[0])}
                          alt={product.name}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CubeIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{product.name}</h4>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.type === 'sale' ? 'bg-green-100 text-green-700' :
                            product.type === 'exchange' ? 'bg-blue-100 text-blue-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {product.type === 'sale' ? t('products.forSale') :
                             product.type === 'exchange' ? t('products.forExchange') : t('lab.assets')}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.status === 'active' ? 'bg-green-100 text-green-700' :
                            product.status === 'inactive' ? 'bg-gray-100 text-gray-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {product.status === 'active' ? t('common.active') :
                             product.status === 'inactive' ? t('common.inactive') : t('common.deleted')}
                          </span>
                        </div>
                        {product.type === 'sale' && (
                          <p className="text-xs text-brand-blue font-medium mt-1">
                            {product.price?.toLocaleString()} {t('products.sar')}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {t('productDetail.quantity')}: {product.quantity || 0}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>

          {/* Recent Sales, Purchases & Exchanges */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('labDashboard.recentActivities')}</h3>
              <a 
                href="/lab/orders" 
                className="text-brand-blue hover:text-blue-700 text-sm font-medium flex items-center"
              >
                {t('labDashboard.viewAllOrders')}
                <ArrowTrendingUpIcon className="h-4 w-4 icon-text" />
              </a>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Sales */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-4 flex items-center gap-2">
                  <BanknotesIcon className="h-5 w-5 text-green-600" />
                  {t('labDashboard.recentSales')}
                </h4>
                <div className="space-y-3">
                  {myOrders.filter(order => order.role === 'seller' && order.type === 'order').length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <BanknotesIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">{t('labDashboard.noSales')}</p>
                    </div>
                  ) : (
                    myOrders.filter(order => order.role === 'seller' && order.type === 'order').slice(0, 3).map((order) => (
                      <div key={order._id} className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-100">
                        <div>
                          <p className="text-sm font-medium text-gray-900">#{order.orderNumber}</p>
                          <p className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleDateString('ar-SA')}</p>
                          <p className="text-xs text-green-600 font-medium">{order.totalAmount?.toLocaleString()} {t('products.sar')}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'completed' ? 'bg-green-500 text-white' :
                          order.status === 'delivered' ? 'bg-emerald-500 text-white' :
                          order.status === 'shipped' ? 'bg-blue-500 text-white' :
                          order.status === 'confirmed' ? 'bg-indigo-500 text-white' :
                          order.status === 'pending' ? 'bg-yellow-500 text-white' :
                          'bg-gray-500 text-white'
                        }`}>
                          {t(`lab.orderStatus.${order.status}`) || order.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Recent Purchases */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-4 flex items-center gap-2">
                  <ShoppingBagIcon className="h-5 w-5 text-blue-600" />
                  {t('labDashboard.recentPurchases')}
                </h4>
                <div className="space-y-3">
                  {myOrders.filter(order => order.role === 'buyer' && order.type === 'order').length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <ShoppingBagIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">{t('labDashboard.noPurchases')}</p>
                    </div>
                  ) : (
                    myOrders.filter(order => order.role === 'buyer' && order.type === 'order').slice(0, 3).map((order) => (
                      <div key={order._id} className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100">
                        <div>
                          <p className="text-sm font-medium text-gray-900">#{order.orderNumber}</p>
                          <p className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleDateString('ar-SA')}</p>
                          <p className="text-xs text-blue-600 font-medium">{order.totalAmount?.toLocaleString()} {t('products.sar')}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'completed' ? 'bg-green-500 text-white' :
                          order.status === 'delivered' ? 'bg-emerald-500 text-white' :
                          order.status === 'shipped' ? 'bg-blue-500 text-white' :
                          order.status === 'confirmed' ? 'bg-indigo-500 text-white' :
                          order.status === 'pending' ? 'bg-yellow-500 text-white' :
                          'bg-gray-500 text-white'
                        }`}>
                          {t(`lab.orderStatus.${order.status}`) || order.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Recent Exchanges */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-4 flex items-center gap-2">
                  <ArrowPathIcon className="h-5 w-5 text-purple-600" />
                  {t('labDashboard.recentExchanges')}
                </h4>
                <div className="space-y-3">
                  {myOrders.filter(order => order.type === 'exchange_request' || order.type === 'exchange_transaction').length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <ArrowPathIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">{t('labDashboard.noExchanges')}</p>
                    </div>
                  ) : (
                    myOrders.filter(order => order.type === 'exchange_request' || order.type === 'exchange_transaction').slice(0, 3).map((order) => (
                      <div key={order._id} className="flex items-center justify-between p-3 rounded-lg bg-purple-50 border border-purple-100">
                        <div>
                          <p className="text-sm font-medium text-gray-900">#{order.orderNumber}</p>
                          <p className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleDateString('ar-SA')}</p>
                          <p className="text-xs text-purple-600 font-medium">
                            {order.type === 'exchange_request' ? 
                              (order.role === 'requester' ? t('labDashboard.sentRequest') : t('labDashboard.receivedRequest')) :
                              order.type === 'exchange_transaction' ?
                              t('labDashboard.activeExchange') :
                              (order.role === 'requester' ? t('labDashboard.requester') : t('labDashboard.receiver'))
                            }
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          // حالات طلبات التبادل
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'viewed' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          order.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          order.status === 'payment_required' ? 'bg-purple-100 text-purple-800' :
                          // حالات المعاملات النشطة
                          order.status === 'confirmed' ? 'bg-indigo-100 text-indigo-800' :
                          order.status === 'shipping_a_to_b' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'shipping_b_to_a' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'both_shipping' ? 'bg-cyan-100 text-cyan-800' :
                          order.status === 'completed' ? 'bg-green-100 text-green-800' :
                          order.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {t(`orderStatuses.${order.status}`) || order.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LabDashboard;
